import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useWhitelabel } from '../../contexts/WhitelabelContext';
import { initializeCsrf, fetchWithCsrf } from '../../utils/csrf';

interface InvitationDetails {
  email: string;
  role: string;
  invitedBy: {
    name: string;
    email: string;
  } | null;
  expiresAt: string | null;
}

export default function AcceptInvitation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { branding } = useWhitelabel();
  const [token, setToken] = useState('');
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const tokenParam = searchParams.get('token');

    if (!tokenParam) {
      setError('Invalid invitation link. No token provided.');
      setIsLoading(false);
      return;
    }

    setToken(tokenParam);
    validateToken(tokenParam);
  }, [searchParams]);

  const validateToken = async (tokenValue: string) => {
    try {
      await initializeCsrf();

      const response = await fetchWithCsrf('/api/invitations/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ token: tokenValue }),
      });

      const data = await response.json();

      if (response.ok && data.valid) {
        setInvitation(data.invitation);
      } else {
        setError(data.error || 'Invalid or expired invitation.');
      }
    } catch (err) {
      console.error('Error validating invitation:', err);
      setError('Failed to validate invitation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== passwordConfirmation) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    setIsSubmitting(true);

    try {
      await initializeCsrf();

      const response = await fetchWithCsrf('/api/invitations/accept', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          token,
          first_name: firstName,
          last_name: lastName,
          password,
          password_confirmation: passwordConfirmation,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(data.message || 'Account created successfully!');
        navigate('/');
      } else {
        if (data.errors) {
          Object.keys(data.errors).forEach(key => {
            data.errors[key].forEach((message: string) => {
              toast.error(message);
            });
          });
        } else {
          toast.error(data.error || 'Failed to accept invitation');
        }
      }
    } catch (err) {
      console.error('Error accepting invitation:', err);
      toast.error('Failed to accept invitation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white text-xl">Validating invitation...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <div className="flex flex-col items-center justify-center mb-6">
            {branding.hasWhitelabel && branding.companyLogo ? (
              <img
                className="max-h-20 max-w-[300px] object-contain mb-4"
                src={branding.companyLogo}
                alt={branding.companyName}
                style={{ maxHeight: '80px', maxWidth: '300px' }}
              />
            ) : (
              <img
                className="h-16 w-auto mb-3"
                src="/assets/img/optimancer_logo.svg"
                alt={branding.companyName}
              />
            )}
            <h2 className="text-2xl font-bold text-white">{branding.companyName}</h2>
          </div>
          <div className="bg-red-900/30 border border-red-700 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-red-400 mb-2">
              Invitation Error
            </h3>
            <p className="text-slate-300 mb-4">{error}</p>
            <p className="text-slate-400 text-sm mb-4">
              If you already have an account, you can use the "Forgot Password"
              option on the sign-in page to reset your password.
            </p>
          </div>
          <a
            href="/sign-in"
            className="inline-block font-medium text-blue-400 hover:text-blue-300"
          >
            Go to Sign In
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex flex-col items-center justify-center mb-6">
            {branding.hasWhitelabel && branding.companyLogo ? (
              <img
                className="max-h-20 max-w-[300px] object-contain mb-4"
                src={branding.companyLogo}
                alt={branding.companyName}
                style={{ maxHeight: '80px', maxWidth: '300px' }}
              />
            ) : (
              <img
                className="h-16 w-auto mb-3"
                src="/assets/img/optimancer_logo.svg"
                alt={branding.companyName}
              />
            )}
            <h2 className="text-2xl font-bold text-white text-center">
              {branding.companyName}
            </h2>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            Join the Team
          </h2>
          {invitation?.invitedBy && (
            <p className="mt-2 text-center text-sm text-slate-400">
              {invitation.invitedBy.name} has invited you to join their team
            </p>
          )}
        </div>

        <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">Email:</span>
            <span className="text-white font-medium">{invitation?.email}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-sm">Role:</span>
            <span className="text-white font-medium capitalize">
              {invitation?.role}
            </span>
          </div>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="first-name" className="sr-only">
                  First Name
                </label>
                <input
                  id="first-name"
                  name="first_name"
                  type="text"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-2 border border-slate-700 placeholder-slate-500 text-white bg-slate-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="First name"
                />
              </div>
              <div>
                <label htmlFor="last-name" className="sr-only">
                  Last Name
                </label>
                <input
                  id="last-name"
                  name="last_name"
                  type="text"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-2 border border-slate-700 placeholder-slate-500 text-white bg-slate-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Last name"
                />
              </div>
            </div>
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={invitation?.email || ''}
                disabled
                className="appearance-none relative block w-full px-3 py-2 border border-slate-700 placeholder-slate-500 text-slate-400 bg-slate-800/50 rounded-md sm:text-sm cursor-not-allowed"
                placeholder="Email address"
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none relative block w-full px-3 py-2 border border-slate-700 placeholder-slate-500 text-white bg-slate-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Create a password (min 8 characters)"
              />
            </div>
            <div>
              <label htmlFor="password-confirmation" className="sr-only">
                Confirm Password
              </label>
              <input
                id="password-confirmation"
                name="password_confirmation"
                type="password"
                required
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                className="appearance-none relative block w-full px-3 py-2 border border-slate-700 placeholder-slate-500 text-white bg-slate-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Confirm your password"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Creating Account...' : 'Create Account & Join Team'}
            </button>
          </div>

          <div className="text-center">
            <p className="text-slate-400 text-sm mb-2">
              Already have an account?
            </p>
            <a
              href="/sign-in"
              className="font-medium text-blue-400 hover:text-blue-300"
            >
              Sign in instead
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
